class CubikaCarousel {
    constructor(containerSelector, options = {}) {
        this.container = document.querySelector(containerSelector);
        if (!this.container) return;

        this.wrapper = this.container.querySelector('.swiper-wrapper'); // Keeping class names for easier migration
        this.slides = Array.from(this.wrapper.querySelectorAll('.swiper-slide'));

        // Options
        this.options = Object.assign({
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            autoplay: false,
            autoplayDelay: 3000,
            effect: 'slide', // 'slide' or 'fade'
            pagination: null, // selector
            navigation: { nextEl: null, prevEl: null },
            breakpoints: {} // { 640: { slidesPerView: 2 } }
        }, options);

        // State
        this.currentIndex = 0;
        this.totalSlides = this.slides.length;
        this.autoPlayInterval = null;
        this.isDragging = false;
        this.startPos = 0;
        this.currentTranslate = 0;
        this.prevTranslate = 0;

        requestAnimationFrame(() => {
            this.init();
            if (this.options.autoplay) {
                this.startAutoplay();
            }
        });
    }

    init() {
        this.updateBreakpoints();

        // Defer all DOM writes to avoid triggering layout during batch initialization
        requestAnimationFrame(() => {
            this.setupStyles();
            this.setupNavigation();
            this.setupPagination();
            this.setupEvents();

            // Trigger initial update after styles are applied
            requestAnimationFrame(() => {
                this.update();
            });
        });

        // Use ResizeObserver for responsive updates instead of 'resize' event + offsetWidth
        this.resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                this.containerWidth = entry.contentRect.width;
                this.update();
            }
        });
        this.resizeObserver.observe(this.container);

        // Initial fade state
        if (this.options.effect === 'fade') {
            this.slides.forEach((slide, index) => {
                slide.style.opacity = index === this.currentIndex ? '1' : '0';
                slide.style.zIndex = index === this.currentIndex ? '10' : '0';
            });
        }
    }

    updateBreakpoints() {
        const width = window.innerWidth;
        this.currentOptions = { ...this.options }; // Reset to base

        // Find matching breakpoint
        const breakpoints = Object.keys(this.options.breakpoints).map(Number).sort((a, b) => a - b);
        for (const bp of breakpoints) {
            if (width >= bp) {
                Object.assign(this.currentOptions, this.options.breakpoints[bp]);
            }
        }
    }

    setupStyles() {
        this.container.style.overflow = 'hidden';
        this.container.style.position = 'relative';

        if (this.currentOptions.effect === 'slide') {
            this.wrapper.style.display = 'flex';
            this.wrapper.style.flexWrap = 'nowrap'; // Ensure no wrapping
            this.wrapper.style.transitionProperty = 'transform';
            this.wrapper.style.transitionTimingFunction = 'ease-out';
            this.wrapper.style.transitionDuration = '0.5s';

            this.slides.forEach(slide => {
                slide.style.flexShrink = '0'; // Absolute prevention of squishing
                slide.style.flexGrow = '0';
            });
        } else if (this.currentOptions.effect === 'fade') {
            this.wrapper.style.position = 'relative';
            this.wrapper.style.height = '100%';
            this.slides.forEach(slide => {
                slide.style.position = 'absolute';
                slide.style.top = '0';
                slide.style.left = '0';
                slide.style.width = '100%';
                slide.style.height = '100%';
                slide.style.transition = 'opacity 0.8s ease-in-out';
            });
        }
    }

    update() {
        if (this.currentOptions.effect === 'fade') return;

        // Re-check breakpoints just in case window size changed but event hasn't fired
        this.updateBreakpoints();

        const slidesPerView = this.currentOptions.slidesPerView;
        const containerWidth = this.containerWidth || this.container.offsetWidth;
        const gap = this.currentOptions.spaceBetween;

        // Accurate width calculation: container shares its width minus gaps between visible slides
        const spaceTotal = (slidesPerView - 1) * gap;
        this.slideWidth = Math.floor((containerWidth - spaceTotal) / slidesPerView);

        // Apply styles in one batch
        requestAnimationFrame(() => {
            this.slides.forEach((slide, index) => {
                slide.style.width = `${this.slideWidth}px`;
                slide.style.flexShrink = '0';
                // Only add margin if it's not the last slide of the whole set
                // or just leave it for all if using transform correctly
                slide.style.marginRight = `${gap}px`;
            });
            this.moveTo(this.currentIndex, true); // Update transform
        });
    }

    moveTo(index, instant = false) {
        // Bounds check
        if (index < 0) index = 0;

        const maxIndex = this.totalSlides - this.currentOptions.slidesPerView;
        if (index > maxIndex) {
            index = this.options.loop ? 0 : maxIndex;
        }

        this.currentIndex = index;

        if (this.options.effect === 'slide') {
            const gap = this.currentOptions.spaceBetween;
            const moveAmt = (this.slideWidth + gap) * this.currentIndex;

            requestAnimationFrame(() => {
                this.wrapper.style.transitionDuration = instant ? '0s' : '0.5s';
                this.wrapper.style.transitionTimingFunction = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
                this.wrapper.style.transform = `translateX(-${moveAmt}px)`;
                this.updatePagination();
            });
        } else if (this.options.effect === 'fade') {
            this.slides.forEach((slide, i) => {
                const isActive = i === this.currentIndex;
                slide.style.opacity = isActive ? '1' : '0';
                slide.style.zIndex = isActive ? '10' : '0';

                // Handle inner content animation for Hero
                const innerContent = slide.querySelector('div');
                if (innerContent) {
                    innerContent.style.transform = isActive ? 'scale(1.1)' : 'scale(1)';
                }
            });
        }

        this.updatePagination();
    }

    next() {
        if (this.options.effect === 'fade') {
            let nextIndex = this.currentIndex + 1;
            if (nextIndex >= this.totalSlides) nextIndex = 0;
            this.moveTo(nextIndex);
        } else {
            // For slide, strictly check bounds or rewind
            let nextIndex = this.currentIndex + 1;
            const maxIndex = this.totalSlides - this.currentOptions.slidesPerView;

            if (this.currentIndex >= maxIndex) {
                if (this.options.loop) nextIndex = 0;
                else nextIndex = maxIndex;
            }
            this.moveTo(nextIndex);
        }
    }

    prev() {
        let prevIndex = this.currentIndex - 1;
        if (prevIndex < 0) {
            if (this.options.loop) {
                if (this.options.effect === 'fade') prevIndex = this.totalSlides - 1;
                else prevIndex = this.totalSlides - this.currentOptions.slidesPerView;
            } else {
                prevIndex = 0;
            }
        }
        this.moveTo(prevIndex);
    }

    setupNavigation() {
        const { nextEl, prevEl } = this.options.navigation;
        if (nextEl) {
            const btn = document.querySelector(nextEl);
            if (btn) btn.addEventListener('click', () => {
                this.stopAutoplay();
                this.next();
            });
        }
        if (prevEl) {
            const btn = document.querySelector(prevEl);
            if (btn) btn.addEventListener('click', () => {
                this.stopAutoplay();
                this.prev();
            });
        }
    }

    setupPagination() {
        if (!this.options.pagination || !this.options.pagination.el) return;

        const paginationContainer = document.querySelector(this.options.pagination.el);
        if (!paginationContainer) return;

        // Clear existing
        paginationContainer.innerHTML = '';

        let dotsCount;
        if (this.options.effect === 'fade') {
            dotsCount = this.totalSlides;
        } else {
            // Calculate how many 'pages' or 'start positions' are possible
            // e.g., if total=8, view=3, maxIndex=5 (indices 0,1,2,3,4,5 are start positions)
            // So we need maxIndex + 1 dots
            const maxIndex = this.totalSlides - this.currentOptions.slidesPerView;
            dotsCount = (maxIndex >= 0 ? maxIndex : 0) + 1;
        }

        for (let i = 0; i < dotsCount; i++) {
            const dot = document.createElement('span');
            dot.classList.add('swiper-pagination-bullet');
            dot.addEventListener('click', () => {
                this.stopAutoplay();
                this.moveTo(i);
            });
            paginationContainer.appendChild(dot);
        }

        // Initial highlight
        this.updatePagination();
    }

    updatePagination() {
        if (!this.options.pagination || !this.options.pagination.el) return;
        const container = document.querySelector(this.options.pagination.el);
        if (!container) return;

        const dots = container.querySelectorAll('.swiper-pagination-bullet');
        // Ensure index matches available dots (in case resize changed count but updatePagination called after)
        dots.forEach((dot, index) => {
            if (index === this.currentIndex) {
                dot.classList.add('swiper-pagination-bullet-active');
            } else {
                dot.classList.remove('swiper-pagination-bullet-active');
            }
        });
    }

    startAutoplay() {
        if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = setInterval(() => {
            this.next();
        }, this.options.autoplay.delay || 3000);
    }

    stopAutoplay() {
        if (this.autoPlayInterval) clearInterval(this.autoPlayInterval);
        // Optional: restart after some time? For now, manual override stops it.
        // Or simpler: restart on mouseleave
    }

    setupEvents() {
        window.addEventListener('resize', () => {
            this.updateBreakpoints();
            // No need to call update() here, ResizeObserver handles it
            this.setupPagination();
            this.updatePagination();
        });

        // Touch support (Basic Swipe)
        this.container.addEventListener('touchstart', (e) => {
            this.startPos = e.touches[0].clientX;
            this.isDragging = true;
            this.stopAutoplay();
        }, { passive: true });

        this.container.addEventListener('touchmove', (e) => {
            if (!this.isDragging) return;
            // Optional: realtime tracking could go here
        }, { passive: true });

        this.container.addEventListener('touchend', (e) => {
            this.isDragging = false;
            const endPos = e.changedTouches[0].clientX;
            const diff = this.startPos - endPos;

            if (Math.abs(diff) > 50) { // Threshold
                if (diff > 0) this.next();
                else this.prev();
            }
        });

        // Pause on hover
        this.container.addEventListener('mouseenter', () => this.stopAutoplay());
        this.container.addEventListener('mouseleave', () => {
            if (this.options.autoplay) this.startAutoplay();
        });
    }
}

// Global export
window.CubikaCarousel = CubikaCarousel;
